import {
  Subject, Observable, BehaviorSubject, Subscription,
} from 'rxjs';
import { v4 as uuids4 } from 'uuid';

import EventType        from './EventType';
import { IEventHub }    from './IEventHub';
import { IEvent }       from './IEvent';

/**
 * Represents a mechanism that allows different components to communicate with
 * each other without knowing about each other.
 */
class EventHub implements IEventHub {
  /**
   * The BehaviourSubject prefix character for the event topic.
   */
  private topicBehaviourSubjectPrefix = '@';

  /**
   * The separator character for the event topic.
   */
  private topicSeparator = ':';

  /**
   * Registry for tracking topics to rxjs subjects
   */
  private registry = new Map<EventType, Subject<IEvent> | BehaviorSubject<IEvent | null>>();

  /**
   * Returns a value indicating whether a given topic is a match.
   *
   * @param topic Event topic.
   * @param wildcard Value to match.
   * @returns `true` if the topic is a match; otherwise, `false`.
   */
  protected topicMatch(topic: EventType, wildcard: string = ''): boolean {
    const w = '*';
    const ww = '**';

    const partMatch = (wl: string, k: string): boolean => wl === w || wl === k;

    const sep = this.topicSeparator;
    const kArr = String(topic).split(sep);
    const wArr = wildcard.split(sep);

    const kLen = kArr.length;
    const wLen = wArr.length;
    const max = Math.max(kLen, wLen);

    for (let i = 0; i < max; i++) {
      const cK = kArr[i];
      const cW = wArr[i];

      if (cW === ww && typeof cK !== 'undefined') {
        return true;
      }

      if (!partMatch(cW, cK)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Publish an event to the {EventHub}.
   *
   * @param  topic Topic to identify the message/event.
   * @param  data Additional data sent with the message/event.
   * @param  error Error data associated with the message/event.
   * @throws {Error} `topic` parameter must be a string and must not be empty.
   */
  public publish<T>(topic: EventType, data?: T, error?: Error): void {
    // eslint-disable-next-line no-console
    console.debug(`EventHub ${topic} `, {
      topic,
      data,
      error,
    });

    const event: IEvent = {
      id: uuids4(),
      topic,
      data,
      error,
      timestamp: new Date().toISOString(),
    };

    if (!this.registry.has(topic)) {
      this.registerTopic(topic);
    }

    const subject = this.getSubject(topic);

    if (subject) {
      subject.next(event);
    }
  }

  /**
   * Returns an observable to which you can unsubscribe
   *
   * @param topic Topic to identify the message/event.
   * @return Observable
   */
  public on(topic: EventType) {
    if (!this.registry.has(topic)) {
      this.registerTopic(topic);
    }

    let subject;
    this.registry.forEach((registeredSubject, registeredTopic) => {
      if (this.topicMatch(registeredTopic, topic)) {
        subject = registeredSubject;
      }
    });

    return subject as Observable<IEvent>;
  }

  /**
   * Lists the topics that have been registered
   * @return Array of registered topics
   */
  public getTopics() {
    return [...this.registry.keys()];
  }

  /**
   * Creates a new Subject and maps it a given topic in the registry
   *
   * @param topic Topic to identify the message/event.
   */
  private registerTopic(topic: EventType) {
    if (this.isBehaviourSubjectTopic(topic)) {
      const subject = new BehaviorSubject<IEvent | null>(null);
      this.registry.set(topic, subject);
      return;
    }

    const subject = new Subject<IEvent>();
    this.registry.set(topic, subject);
  }

  private isBehaviourSubjectTopic(topic: EventType): boolean {
    return String(topic).charAt(0) === this.topicBehaviourSubjectPrefix;
  }

  /**
   * Looks up the subject for a given topic
   *
   * @param topic Topic to identify the message/event.
   * @return Subject
   */
  private getSubject(topic: EventType) {
    return this.registry.get(topic);
  }
  private currentLang = 'en';
  
  public  getCurrentLang() {
    return this.currentLang;
  }
  public setCurrentLang(laang: string) {
     this.currentLang=laang;
  }


  public subscribe<T>(topic: EventType, subscription: (data: IEvent<T> | null) => void): Subscription {
    if (this.isBehaviourSubjectTopic(topic)) {
      return this.on(topic).subscribe((data: IEvent | null) => {
        /**
         * Skip the subscription when data is null
         */
        if (!data) {
          return;
        }
        subscription(data);
      });
    }
    return this.on(topic).subscribe(subscription);
  }
}

export { EventHub };
export type { IEventHub };
